import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import './RankTracking.css';
import '../components/SEOAnalyzer.css';

function getDefaultStartDate() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
}

function getDefaultEndDate() {
  return new Date().toISOString().split('T')[0];
}

const RankTracking = ({ user, onLogout, currentProject }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [dateRangeType, setDateRangeType] = useState('30days'); // '7days', '30days', 'custom'
  const [keywords, setKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [visibleMetrics, setVisibleMetrics] = useState({
    clicks: true,
    impressions: true,
    ctr: true,
    position: true
  });
  const [hoveredData, setHoveredData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showCustomDateDialog, setShowCustomDateDialog] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [trackedKeywordsData, setTrackedKeywordsData] = useState([]);
  const [loadingTrackedKeywords, setLoadingTrackedKeywords] = useState(false);
  const [keywordSearchVolumes, setKeywordSearchVolumes] = useState({});
  const [keywordCompetitions, setKeywordCompetitions] = useState({});

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Load connection status from cache on mount (instant display)
  useEffect(() => {
    try {
      const cachedStatus = localStorage.getItem('rankTracking_connectionStatus');
      if (cachedStatus) {
        const parsed = JSON.parse(cachedStatus);
        if (parsed.connected !== undefined) {
          setConnected(parsed.connected);
          
          // Restore properties and selected property from cache
          if (parsed.connected && parsed.properties) {
            setProperties(parsed.properties);
            if (parsed.selectedProperty) {
              setSelectedProperty(parsed.selectedProperty);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading cached connection status:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check connection status from API and update cache
  const refreshConnectionStatus = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      if (!token) {
        setConnected(false);
        localStorage.setItem('rankTracking_connectionStatus', JSON.stringify({
          connected: false,
          timestamp: new Date().toISOString()
        }));
        setLoading(false);
        return;
      }

      // Check if tokens exist in database
      const response = await axios.get(getApiUrl('search-console/status'), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const isConnected = response.data.connected;
      setConnected(isConnected);

      // Update cache
      const cacheData = {
        connected: isConnected,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('rankTracking_connectionStatus', JSON.stringify(cacheData));

      // If connected, load properties (loadProperties will update cache itself)
      if (isConnected) {
        await loadProperties();
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      setConnected(false);
      localStorage.setItem('rankTracking_connectionStatus', JSON.stringify({
        connected: false,
        timestamp: new Date().toISOString()
      }));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get storage key for a keyword and date range
  const getStorageKey = (keyword, dateRange = null) => {
    const keywordPart = keyword || 'all';
    if (dateRange) {
      // Include date range in key so different ranges are cached separately
      return `rankTrackingData_${keywordPart}_${dateRange.startDate}_${dateRange.endDate}`;
    }
    return `rankTrackingData_${keywordPart}`;
  };

  // Helper to load cached performance data for a specific date range
  const loadCachedPerformanceData = useCallback((targetDateRange, targetKeyword) => {
    try {
      const storageKey = getStorageKey(targetKeyword, targetDateRange);
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.performanceData && 
            parsed.selectedProperty === selectedProperty &&
            parsed.dateRange &&
            parsed.dateRange.startDate === targetDateRange.startDate && 
            parsed.dateRange.endDate === targetDateRange.endDate) {
          // Check if data is recent (less than 24 hours old)
          if (parsed.timestamp) {
            const savedTime = new Date(parsed.timestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            if (hoursDiff < 24) {
              setPerformanceData(parsed.performanceData);
              return true; // Found cached data
            }
          } else {
            setPerformanceData(parsed.performanceData);
            return true; // Found cached data
          }
        }
      }
    } catch (err) {
      console.error('Error loading cached performance data:', err);
    }
    return false; // No cached data found
  }, [selectedProperty]);

  // Load saved performance data on component mount only
  useEffect(() => {
    try {
      // Try to load data for current date range and "all" keywords (default)
      // Use the new cache key format that includes date range
      const savedData = localStorage.getItem(getStorageKey(null, dateRange));
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.performanceData && parsed.selectedProperty) {
          // Restore saved state
          setSelectedProperty(parsed.selectedProperty);
          if (parsed.dateRange) {
            setDateRange(parsed.dateRange);
            // Determine date range type based on saved dates
            const endDate = new Date();
            const startDate = new Date(parsed.dateRange.startDate);
            const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
            if (daysDiff === 7) {
              setDateRangeType('7days');
            } else if (daysDiff === 30) {
              setDateRangeType('30days');
            } else {
              setDateRangeType('custom');
            }
          }
          if (parsed.selectedKeyword !== undefined) {
            setSelectedKeyword(parsed.selectedKeyword);
          }
          // Only restore performance data if it's recent (less than 24 hours old)
          if (parsed.timestamp) {
            const savedTime = new Date(parsed.timestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            if (hoursDiff < 24) {
              setPerformanceData(parsed.performanceData);
            } else {
              // Data is stale, clear it
              localStorage.removeItem(getStorageKey(null, parsed.dateRange || dateRange));
            }
          } else {
            setPerformanceData(parsed.performanceData);
          }
        }
      }
    } catch (err) {
      console.error('Error loading saved rank tracking data:', err);
    } finally {
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Load saved data when keyword or date range changes
  useEffect(() => {
    if (!isInitialLoad && selectedProperty && dateRange.startDate && dateRange.endDate) {
      // Use the helper function to load cached data
      if (!loadCachedPerformanceData(dateRange, selectedKeyword)) {
        setPerformanceData(null); // No cache found
      }
    }
  }, [selectedKeyword, selectedProperty, dateRange, isInitialLoad, loadCachedPerformanceData]);

  // Load keywords from current project
  useEffect(() => {
    if (currentProject && currentProject.keywordAnalyses) {
      setKeywords(currentProject.keywordAnalyses);
    } else {
      setKeywords([]);
    }
  }, [currentProject]);

  // Memoize keywords array to prevent unnecessary re-renders
  const keywordsString = useMemo(() => {
    return JSON.stringify(keywords.map(kw => ({ keyword: kw.keyword, targetUrl: kw.targetUrl })));
  }, [keywords]);

  // Ref to track if data has been loaded to prevent duplicate fetches
  const trackedKeywordsDataLoaded = useRef(false);
  const lastLoadParams = useRef({ property: null, keywords: null });

  // Memoized function to check if we should load data
  const shouldLoadTrackedKeywords = useCallback(() => {
    if (!connected || !selectedProperty || keywords.length === 0) {
      return false;
    }

    const currentParams = {
      property: selectedProperty,
      keywords: keywordsString
    };

    // Check if params have changed
    if (
      lastLoadParams.current.property === currentParams.property &&
      lastLoadParams.current.keywords === currentParams.keywords &&
      trackedKeywordsDataLoaded.current
    ) {
      return false; // Same params, already loaded
    }

    // Check cache first
    const cacheKey = `trackedKeywords_${selectedProperty}_${keywordsString}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const cacheTime = new Date(parsed.timestamp);
        const now = new Date();
        const hoursDiff = (now - cacheTime) / (1000 * 60 * 60);
        
        // Use cached data if less than 1 hour old
        if (hoursDiff < 1 && parsed.data) {
          setTrackedKeywordsData(parsed.data);
          setKeywordSearchVolumes(parsed.searchVolumes || {});
          setKeywordCompetitions(parsed.competitions || {});
          trackedKeywordsDataLoaded.current = true;
          lastLoadParams.current = currentParams;
          return false; // Don't fetch, use cache
        }
      } catch (err) {
        console.error('Error loading cached tracked keywords:', err);
      }
    }

    return true; // Should fetch
  }, [connected, selectedProperty, keywordsString, keywords.length]);

  // Load tracked keywords data when property or keywords change (with caching)
  useEffect(() => {
    if (shouldLoadTrackedKeywords()) {
      trackedKeywordsDataLoaded.current = false;
      loadTrackedKeywordsData();
    } else if (!connected || !selectedProperty || keywords.length === 0) {
      setTrackedKeywordsData([]);
      trackedKeywordsDataLoaded.current = false;
      lastLoadParams.current = { property: null, keywords: null };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, selectedProperty, keywordsString, shouldLoadTrackedKeywords]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      // After OAuth callback, refresh connection status and load properties
      refreshConnectionStatus();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (params.get('error')) {
      setError(params.get('error'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Removed automatic data loading - user will click refresh button instead

  // eslint-disable-next-line no-unused-vars
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const getAuthToken = () => {
    return localStorage.getItem('scanmaster_token');
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      const token = getAuthToken();
      
      const response = await axios.get(getApiUrl('search-console/auth'), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Redirect to Google OAuth
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      setError(error.response?.data?.error || 'Failed to connect to Google Search Console. Please check your server configuration.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = getAuthToken();
      await axios.post(getApiUrl('search-console/disconnect'), {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setConnected(false);
      setProperties([]);
      setSelectedProperty(null);
      setPerformanceData(null);
      
      // Clear saved property selection
      localStorage.removeItem('rankTracking_selectedProperty');
    } catch (error) {
      console.error('Error disconnecting:', error);
      setError('Failed to disconnect. Please try again.');
    }
  };

  const loadProperties = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(getApiUrl('search-console/properties'), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const propertiesList = response.data.properties || [];
      setProperties(propertiesList);

      // Restore selected property from localStorage if available
      if (propertiesList.length > 0 && !selectedProperty) {
        const savedProperty = localStorage.getItem('rankTracking_selectedProperty');
        const propertyToSelect = savedProperty && propertiesList.find(p => p.siteUrl === savedProperty)
          ? savedProperty
          : propertiesList[0].siteUrl;
        
        setSelectedProperty(propertyToSelect);
        localStorage.setItem('rankTracking_selectedProperty', propertyToSelect);
      }

      // Update cache with properties
      try {
        const cachedStatus = localStorage.getItem('rankTracking_connectionStatus');
        const cacheData = cachedStatus ? JSON.parse(cachedStatus) : {};
        cacheData.properties = propertiesList;
        cacheData.selectedProperty = selectedProperty || cacheData.selectedProperty;
        localStorage.setItem('rankTracking_connectionStatus', JSON.stringify(cacheData));
      } catch (err) {
        console.error('Error updating cache:', err);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
      if (error.response?.status === 401 || error.message?.includes('No tokens found')) {
        setConnected(false);
        setError('Your Google account connection has expired. Please reconnect.');
        localStorage.setItem('rankTracking_connectionStatus', JSON.stringify({
          connected: false,
          timestamp: new Date().toISOString()
        }));
      } else {
        setError('Failed to load Search Console properties.');
      }
    }
  };

  // Helper to load performance data for a specific date range
  const loadPerformanceDataForRange = async (targetDateRange, targetKeyword) => {
    if (!selectedProperty) return null;

    try {
      const token = getAuthToken();
      const response = await axios.get(getApiUrl('search-console/performance'), {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          propertyUrl: selectedProperty,
          startDate: targetDateRange.startDate,
          endDate: targetDateRange.endDate,
          keyword: targetKeyword || undefined
        }
      });

      const data = response.data;

      // Save to localStorage with keyword and date range specific key
      try {
        const storageKey = getStorageKey(targetKeyword, targetDateRange);
        const dataToSave = {
          performanceData: data,
          selectedProperty: selectedProperty,
          dateRange: targetDateRange,
          selectedKeyword: targetKeyword,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      } catch (err) {
        console.error('Error saving rank tracking data:', err);
      }

      return data;
    } catch (error) {
      console.error(`Error loading performance data for range ${targetDateRange.startDate} to ${targetDateRange.endDate}:`, error);
      return null;
    }
  };

  const loadPerformanceData = async () => {
    if (!selectedProperty) return;

    // Refresh connection status first when refresh button is clicked
    await refreshConnectionStatus();

    try {
      setLoadingPerformance(true);
      setError(null);
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      // Calculate all three date ranges
      const endDate = new Date();
      const today = endDate.toISOString().split('T')[0];
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysRange = {
        startDate: sevenDaysAgo.toISOString().split('T')[0],
        endDate: today
      };

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysRange = {
        startDate: thirtyDaysAgo.toISOString().split('T')[0],
        endDate: today
      };

      // Load data for all date ranges in parallel
      const [sevenDaysData, thirtyDaysData, currentRangeData] = await Promise.all([
        loadPerformanceDataForRange(sevenDaysRange, selectedKeyword),
        loadPerformanceDataForRange(thirtyDaysRange, selectedKeyword),
        loadPerformanceDataForRange(dateRange, selectedKeyword)
      ]);

      // Set the current view to the data for the currently selected date range
      if (currentRangeData) {
        setPerformanceData(currentRangeData);
      } else if (sevenDaysData && dateRange.startDate === sevenDaysRange.startDate && dateRange.endDate === sevenDaysRange.endDate) {
        setPerformanceData(sevenDaysData);
      } else if (thirtyDaysData && dateRange.startDate === thirtyDaysRange.startDate && dateRange.endDate === thirtyDaysRange.endDate) {
        setPerformanceData(thirtyDaysData);
      } else {
        // Fallback: use current range data or show error
        setError('Failed to load performance data. Please try again.');
      }

    } catch (error) {
      console.error('Error loading performance data:', error);
      if (error.response?.status === 401 || error.message?.includes('No tokens found')) {
        setConnected(false);
        setError('Your Google account connection has expired. Please reconnect.');
      } else {
        setError('Failed to load performance data. Please try again.');
      }
    } finally {
      setLoadingPerformance(false);
    }
  };

  // Define loadKeywordSearchVolumes first since loadTrackedKeywordsData depends on it
  const loadKeywordSearchVolumes = useCallback(async (keywordList) => {
    if (!keywordList || keywordList.length === 0) return;

    // Check cache first
    const cacheKey = `keywordVolumes_${keywordList.sort().join(',')}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const cacheTime = new Date(parsed.timestamp);
        const now = new Date();
        const hoursDiff = (now - cacheTime) / (1000 * 60 * 60);
        
        // Use cached data if less than 24 hours old (search volumes don't change frequently)
        if (hoursDiff < 24 && parsed.volumes && parsed.competitions) {
          setKeywordSearchVolumes(parsed.volumes);
          setKeywordCompetitions(parsed.competitions);
          return; // Use cache, don't fetch
        }
      } catch (err) {
        console.error('Error loading cached keyword volumes:', err);
      }
    }

    try {
      const token = getAuthToken();
      const response = await axios.get(getApiUrl('google-ads/keyword-volume'), {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          keywords: keywordList.join(',')
        }
      });

      // Create maps for search volume and competition
      const volumeMap = {};
      const competitionMap = {};
      if (response.data && response.data.keywords) {
        response.data.keywords.forEach(item => {
          volumeMap[item.keyword] = item.searchVolume;
          // Convert competition enum to readable format
          if (item.competition) {
            const competitionStr = item.competition.toString().toUpperCase();
            if (competitionStr === 'LOW' || competitionStr === 'UNSPECIFIED') {
              competitionMap[item.keyword] = 'Low';
            } else if (competitionStr === 'MEDIUM') {
              competitionMap[item.keyword] = 'Medium';
            } else if (competitionStr === 'HIGH') {
              competitionMap[item.keyword] = 'High';
            } else {
              competitionMap[item.keyword] = item.competition;
            }
          } else {
            competitionMap[item.keyword] = null;
          }
        });
        
        // Cache the results
        try {
          const dataToCache = {
            volumes: volumeMap,
            competitions: competitionMap,
            timestamp: new Date().toISOString()
          };
          localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (err) {
          console.error('Error caching keyword volumes:', err);
        }
      }
      
      setKeywordSearchVolumes(volumeMap);
      setKeywordCompetitions(competitionMap);
    } catch (error) {
      console.error('Error loading keyword search volumes:', error);
      // Don't throw - just log, as this is optional data
    }
  }, []);

  const loadTrackedKeywordsData = useCallback(async () => {
    if (!selectedProperty || !keywords.length) {
      setTrackedKeywordsData([]);
      return;
    }

    // Check if already loading to prevent duplicate requests
    if (loadingTrackedKeywords) {
      return;
    }

    try {
      setLoadingTrackedKeywords(true);
      const token = getAuthToken();
      
      // Calculate date range for last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const last30DaysStart = startDate.toISOString().split('T')[0];
      const last30DaysEnd = endDate.toISOString().split('T')[0];

      // Fetch data for each keyword
      const keywordDataPromises = keywords.map(async (kw) => {
        try {
          const response = await axios.get(getApiUrl('search-console/performance'), {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              propertyUrl: selectedProperty,
              startDate: last30DaysStart,
              endDate: last30DaysEnd,
              keyword: kw.keyword || undefined
            }
          });

          const data = response.data;
          return {
            keyword: kw.keyword,
            targetUrl: kw.targetUrl || '',
            position: data.position || 0,
            volume: data.impressions || 0
          };
        } catch (error) {
          console.error(`Error loading data for keyword "${kw.keyword}":`, error);
          return {
            keyword: kw.keyword,
            targetUrl: kw.targetUrl || '',
            position: 0,
            volume: 0
          };
        }
      });

      const results = await Promise.all(keywordDataPromises);
      setTrackedKeywordsData(results);

      // Fetch real search volume from Google Ads API
      await loadKeywordSearchVolumes(keywords.map(kw => kw.keyword));

      // Get the updated volumes and competitions after loadKeywordSearchVolumes completes
      // We'll cache them in the next render cycle, but for now just cache the results
      const cacheKey = `trackedKeywords_${selectedProperty}_${keywordsString}`;
      const dataToCache = {
        data: results,
        timestamp: new Date().toISOString()
      };
      try {
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      } catch (err) {
        console.error('Error caching tracked keywords data:', err);
      }

      // Mark as loaded
      trackedKeywordsDataLoaded.current = true;
      lastLoadParams.current = {
        property: selectedProperty,
        keywords: keywordsString
      };
    } catch (error) {
      console.error('Error loading tracked keywords data:', error);
      setTrackedKeywordsData([]);
      trackedKeywordsDataLoaded.current = false;
    } finally {
      setLoadingTrackedKeywords(false);
    }
  }, [selectedProperty, keywords, keywordsString, loadingTrackedKeywords, loadKeywordSearchVolumes]);

  const handleDateRangeChange = (type) => {
    const endDate = new Date();
    const startDate = new Date();
    
    if (type === '7days') {
      startDate.setDate(startDate.getDate() - 7);
      const newDateRange = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
      setDateRange(newDateRange);
      setDateRangeType('7days');
      // useEffect will handle loading cached data
    } else if (type === '30days') {
      startDate.setDate(startDate.getDate() - 30);
      const newDateRange = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
      setDateRange(newDateRange);
      setDateRangeType('30days');
      // useEffect will handle loading cached data
    } else if (type === 'custom') {
      // Show dialog for custom date range
      setTempDateRange(dateRange); // Initialize with current date range
      setShowCustomDateDialog(true);
    }
  };

  const handleCustomDateConfirm = () => {
    setDateRange(tempDateRange);
    setDateRangeType('custom');
    setShowCustomDateDialog(false);
    // useEffect will handle loading cached data
  };

  const handleCustomDateCancel = () => {
    setShowCustomDateDialog(false);
    // Reset temp date range to current date range
    setTempDateRange(dateRange);
  };

  const handlePropertyChange = (e) => {
    const propertyUrl = e.target.value;
    setSelectedProperty(propertyUrl);
    
    // Save selected property to localStorage for persistence
    try {
      localStorage.setItem('rankTracking_selectedProperty', propertyUrl);
      
      // Also update the connection status cache with the selected property
      const cachedStatus = localStorage.getItem('rankTracking_connectionStatus');
      if (cachedStatus) {
        const parsed = JSON.parse(cachedStatus);
        parsed.selectedProperty = propertyUrl;
        localStorage.setItem('rankTracking_connectionStatus', JSON.stringify(parsed));
      }
    } catch (err) {
      console.error('Error saving selected property:', err);
    }
    
    // Clear performance data when property changes
    setPerformanceData(null);
    // Clear all saved data for this property change
    // Clear all keyword-specific data
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('rankTrackingData_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (err) {
      console.error('Error clearing saved data:', err);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatPercentage = (num) => {
    return (num * 100).toFixed(2) + '%';
  };

  const header = (
    <header className="header">
      <div className="header-container">
        <div className="header-actions">
          <a href="/pricing" className="header-menu-item">
            Plans & Pricing
          </a>
          {user && (
            <div className="user-info">
              <button
                className="user-icon-button"
                onClick={() => {
                  window.location.href = '/dashboard/account-settings';
                }}
                aria-label="Account settings"
                title="Account settings"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="seo-analyzer rank-tracking-page">
        {header}
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Checking connection status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seo-analyzer rank-tracking-page">
      {header}
      <div className="container">
        {error && (
          <div className="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {!connected ? (
          <div className="connection-prompt">
            <div className="connection-icon">
              <svg viewBox="-3 0 262 262" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" fill="#000000" width="80" height="80">
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" fill="#4285F4"></path>
                  <path d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" fill="#34A853"></path>
                  <path d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782" fill="#FBBC05"></path>
                  <path d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" fill="#EB4335"></path>
                </g>
              </svg>
            </div>
            <h2>Connect with Google Search Console</h2>
            <p>Connect your Google Search Console account to view your website's search performance data, including clicks, impressions, CTR, and average position.</p>
            <button 
              className="connect-button"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" fill="white" width="20" height="20">
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      <title>google [#178]</title>
                      <desc>Created with Sketch.</desc>
                      <defs></defs>
                      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g id="Dribbble-Light-Preview" transform="translate(-300.000000, -7399.000000)" fill="white">
                          <g id="icons" transform="translate(56.000000, 160.000000)">
                            <path d="M263.821537,7247.00386 L254.211298,7247.00386 C254.211298,7248.0033 254.211298,7250.00218 254.205172,7251.00161 L259.774046,7251.00161 C259.560644,7252.00105 258.804036,7253.40026 257.734984,7254.10487 C257.733963,7254.10387 257.732942,7254.11086 257.7309,7254.10986 C256.309581,7255.04834 254.43389,7255.26122 253.041161,7254.98137 C250.85813,7254.54762 249.130492,7252.96451 248.429023,7250.95364 C248.433107,7250.95064 248.43617,7250.92266 248.439233,7250.92066 C248.000176,7249.67336 248.000176,7248.0033 248.439233,7247.00386 L248.438212,7247.00386 C249.003881,7245.1669 250.783592,7243.49084 252.969687,7243.0321 C254.727956,7242.65931 256.71188,7243.06308 258.170978,7244.42831 C258.36498,7244.23842 260.856372,7241.80579 261.043226,7241.6079 C256.0584,7237.09344 248.076756,7238.68155 245.090149,7244.51127 L245.089128,7244.51127 C245.089128,7244.51127 245.090149,7244.51127 245.084023,7244.52226 L245.084023,7244.52226 C243.606545,7247.38565 243.667809,7250.75975 245.094233,7253.48622 C245.090149,7253.48921 245.087086,7253.49121 245.084023,7253.49421 C246.376687,7256.0028 248.729215,7257.92672 251.563684,7258.6593 C254.574796,7259.44886 258.406843,7258.90916 260.973794,7256.58747 C260.974815,7256.58847 260.975836,7256.58947 260.976857,7256.59047 C263.15172,7254.63157 264.505648,7251.29445 263.821537,7247.00386" id="google-[#178]"></path>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>
            <div className="connection-info">
              <p className="info-note">
                <strong>Note:</strong> You'll be redirected to Google to authorize access. 
                We only request read-only access to your Search Console data.
              </p>
            </div>
          </div>
        ) : (
          <div className="rank-tracking-content">
            <div className="rank-tracking-header">
              <h1 className="page-title">Rank Tracking</h1>
              <div className="header-buttons">
                <button
                  className="disconnect-button"
                  onClick={handleDisconnect}
                  title="Disconnect Google account"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Disconnect</span>
                </button>
                <button
                  className="refresh-button"
                  onClick={loadPerformanceData}
                  disabled={loadingPerformance || !selectedProperty}
                  title="Refresh performance data"
                >
                  {loadingPerformance ? (
                    <>
                      <div className="button-spinner-small"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                      </svg>
                      <span>Refresh</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="controls-section">
              <div className="control-group">
                <label htmlFor="property-select">Select Property</label>
                <select
                  id="property-select"
                  value={selectedProperty || ''}
                  onChange={handlePropertyChange}
                  className="property-select"
                >
                  {properties.length === 0 ? (
                    <option value="">Loading properties...</option>
                  ) : (
                    <>
                      <option value="">Select a property</option>
                      {properties.map((property) => (
                        <option key={property.siteUrl} value={property.siteUrl}>
                          {property.siteUrl} {property.permissionLevel ? `(${property.permissionLevel})` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div className="control-group">
                <label>Date Range</label>
                <div className="date-range-buttons">
                  <button
                    type="button"
                    className={`date-range-btn ${dateRangeType === '7days' ? 'active' : ''}`}
                    onClick={() => handleDateRangeChange('7days')}
                  >
                    Last 7 Days
                  </button>
                  <button
                    type="button"
                    className={`date-range-btn ${dateRangeType === '30days' ? 'active' : ''}`}
                    onClick={() => handleDateRangeChange('30days')}
                  >
                    Last 30 Days
                  </button>
                  <button
                    type="button"
                    className={`date-range-btn ${dateRangeType === 'custom' ? 'active' : ''}`}
                    onClick={() => handleDateRangeChange('custom')}
                  >
                    Custom
                  </button>
                </div>
              </div>
            </div>

            {/* Custom Date Range Dialog */}
            {showCustomDateDialog && (
              <div className="modal-overlay" onClick={handleCustomDateCancel}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Select Custom Date Range</h3>
                    <button
                      className="modal-close"
                      onClick={handleCustomDateCancel}
                      aria-label="Close"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="custom-date-inputs">
                      <div className="custom-date-group">
                        <label htmlFor="dialog-start-date">Start Date</label>
                        <input
                          id="dialog-start-date"
                          type="date"
                          value={tempDateRange.startDate}
                          onChange={(e) => {
                            setTempDateRange({ ...tempDateRange, startDate: e.target.value });
                          }}
                          className="date-input"
                          max={tempDateRange.endDate}
                        />
                      </div>
                      <div className="custom-date-group">
                        <label htmlFor="dialog-end-date">End Date</label>
                        <input
                          id="dialog-end-date"
                          type="date"
                          value={tempDateRange.endDate}
                          onChange={(e) => {
                            setTempDateRange({ ...tempDateRange, endDate: e.target.value });
                          }}
                          className="date-input"
                          min={tempDateRange.startDate}
                          max={getDefaultEndDate()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="modal-button secondary"
                      onClick={handleCustomDateCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="modal-button primary"
                      onClick={handleCustomDateConfirm}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {connected && keywords.length > 0 && (
              <div className="keyword-ranking-section">
                <h2 className="keyword-ranking-title">Keyword Focused Ranking</h2>
                <div className="keywords-box">
                  <div className="keywords-tags">
                    <div
                      className={`keyword-tag ${selectedKeyword === null ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedKeyword(null);
                        // Data will be loaded automatically via useEffect
                      }}
                    >
                      <span className="keyword-tag-text">All Keywords</span>
                    </div>
                    {keywords.map((kw) => (
                      <div
                        key={kw.id}
                        className={`keyword-tag ${selectedKeyword === kw.keyword ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedKeyword(kw.keyword);
                          // Data will be loaded automatically via useEffect
                        }}
                      >
                        <span className="keyword-tag-text">{kw.keyword}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}


            {selectedProperty && (
              <div className="performance-section">
                {!performanceData && !loadingPerformance ? (
                  <div className="no-data-prompt">
                    <p>Click "Refresh" to load performance data for the selected property and date range.</p>
                  </div>
                ) : loadingPerformance ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading performance data...</p>
                  </div>
                ) : performanceData ? (
                  <>
                    {selectedKeyword && (
                      <div className="keyword-filter-indicator">
                        <span>Showing results for keyword: <strong>{selectedKeyword}</strong></span>
                        <button
                          className="clear-keyword-filter"
                          onClick={() => {
                            setSelectedKeyword(null);
                            // Data will be loaded automatically via useEffect
                          }}
                        >
                          Clear filter
                        </button>
                      </div>
                    )}
                    <div className="metrics-grid">
                      <div className="metric-card">
                        <label className="metric-checkbox">
                          <input
                            type="checkbox"
                            checked={visibleMetrics.clicks}
                            onChange={(e) => setVisibleMetrics({ ...visibleMetrics, clicks: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                        </label>
                        <div className="metric-label">Total Clicks</div>
                        <div className="metric-value">{formatNumber(performanceData.clicks)}</div>
                        <div className="metric-description">Total clicks from search results</div>
                      </div>
                      <div className="metric-card">
                        <label className="metric-checkbox">
                          <input
                            type="checkbox"
                            checked={visibleMetrics.impressions}
                            onChange={(e) => setVisibleMetrics({ ...visibleMetrics, impressions: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                        </label>
                        <div className="metric-label">Total Impressions</div>
                        <div className="metric-value">{formatNumber(performanceData.impressions)}</div>
                        <div className="metric-description">How many times your site appeared in search</div>
                      </div>
                      <div className="metric-card">
                        <label className="metric-checkbox">
                          <input
                            type="checkbox"
                            checked={visibleMetrics.ctr}
                            onChange={(e) => setVisibleMetrics({ ...visibleMetrics, ctr: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                        </label>
                        <div className="metric-label">Average CTR</div>
                        <div className="metric-value">{formatPercentage(performanceData.ctr)}</div>
                        <div className="metric-description">Click-through rate</div>
                      </div>
                      <div className="metric-card">
                        <label className="metric-checkbox">
                          <input
                            type="checkbox"
                            checked={visibleMetrics.position}
                            onChange={(e) => setVisibleMetrics({ ...visibleMetrics, position: e.target.checked })}
                          />
                          <span className="checkbox-custom"></span>
                        </label>
                        <div className="metric-label">Average Position</div>
                        <div className="metric-value">{performanceData.position.toFixed(1)}</div>
                        <div className="metric-description">Average ranking position</div>
                      </div>
                    </div>

                    {performanceData.dailyData && performanceData.dailyData.length > 0 && (
                      <div className="chart-section">
                        <h3 className="chart-title">
                          Performance Over Time
                          {selectedKeyword && (
                            <span className="chart-keyword-badge">for "{selectedKeyword}"</span>
                          )}
                        </h3>
                        <div className="chart-container">
                          <div className="line-chart-wrapper">
                            <svg 
                              className="line-chart" 
                              viewBox="0 0 1000 300" 
                              preserveAspectRatio="xMidYMid meet"
                              onMouseMove={(e) => {
                                if (!performanceData.dailyData || performanceData.dailyData.length === 0) return;
                                
                                const svg = e.currentTarget;
                                const rect = svg.getBoundingClientRect();
                                
                                // Calculate mouse position relative to SVG
                                const x = ((e.clientX - rect.left) / rect.width) * 1000;
                                
                                const padding = 20;
                                const chartWidth = 1000 - padding * 2;
                                const dataLength = performanceData.dailyData.length;
                                
                                if (x >= padding && x <= 1000 - padding) {
                                  const relativeX = x - padding;
                                  const index = Math.round((relativeX / chartWidth) * (dataLength - 1));
                                  const clampedIndex = Math.max(0, Math.min(index, dataLength - 1));
                                  const dayData = performanceData.dailyData[clampedIndex];
                                  
                                  setHoveredData(dayData);
                                  setTooltipPosition({ x: e.clientX, y: e.clientY });
                                } else {
                                  setHoveredData(null);
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredData(null);
                              }}
                            >
                              <defs>
                                <linearGradient id="clicksGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="var(--brand-color)" stopOpacity="0.2" />
                                  <stop offset="100%" stopColor="var(--brand-color)" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="impressionsGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.2" />
                                  <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="ctrGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="#34a853" stopOpacity="0.2" />
                                  <stop offset="100%" stopColor="#34a853" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="positionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="#9c27b0" stopOpacity="0.2" />
                                  <stop offset="100%" stopColor="#9c27b0" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              {(() => {
                                const width = 1000;
                                const height = 280;
                                const padding = 20;
                                const chartWidth = width - padding * 2;
                                const chartHeight = height - padding * 2;
                                const dataLength = performanceData.dailyData.length;
                                const stepX = dataLength > 1 ? chartWidth / (dataLength - 1) : 0;
                                
                                // Normalize all values to 0-1 range for visual comparison
                                const maxClicks = Math.max(...performanceData.dailyData.map(d => d.clicks), 1);
                                const maxImpressions = Math.max(...performanceData.dailyData.map(d => d.impressions), 1);
                                const maxCTR = Math.max(...performanceData.dailyData.map(d => d.ctr), 0.01); // CTR as decimal
                                const maxPosition = Math.max(...performanceData.dailyData.map(d => d.position), 1);
                                
                                // Normalize function - converts any value to 0-1 range based on its max
                                const normalize = (value, max) => max > 0 ? value / max : 0;
                                
                                // Get Y position (0-1 normalized value, inverted for SVG coordinates)
                                const getY = (normalizedValue) => {
                                  return padding + chartHeight - (normalizedValue * chartHeight);
                                };
                                
                                const clicksPoints = performanceData.dailyData.map((day, i) => 
                                  `${padding + i * stepX},${getY(normalize(day.clicks, maxClicks))}`
                                ).join(' ');
                                
                                const impressionsPoints = performanceData.dailyData.map((day, i) => 
                                  `${padding + i * stepX},${getY(normalize(day.impressions, maxImpressions))}`
                                ).join(' ');
                                
                                const ctrPoints = performanceData.dailyData.map((day, i) => 
                                  `${padding + i * stepX},${getY(normalize(day.ctr, maxCTR))}`
                                ).join(' ');
                                
                                const positionPoints = performanceData.dailyData.map((day, i) => {
                                  // Invert position (lower is better, so we flip it)
                                  const normalizedPos = normalize(day.position, maxPosition);
                                  return `${padding + i * stepX},${getY(1 - normalizedPos)}`;
                                }).join(' ');
                                
                                const clicksArea = `M ${padding},${height - padding} L ${clicksPoints} L ${padding + (dataLength - 1) * stepX},${height - padding} Z`;
                                const impressionsArea = `M ${padding},${height - padding} L ${impressionsPoints} L ${padding + (dataLength - 1) * stepX},${height - padding} Z`;
                                const ctrArea = `M ${padding},${height - padding} L ${ctrPoints} L ${padding + (dataLength - 1) * stepX},${height - padding} Z`;
                                const positionArea = `M ${padding},${height - padding} L ${positionPoints} L ${padding + (dataLength - 1) * stepX},${height - padding} Z`;
                                
                                const clicksPath = `M ${clicksPoints}`;
                                const impressionsPath = `M ${impressionsPoints}`;
                                const ctrPath = `M ${ctrPoints}`;
                                const positionPath = `M ${positionPoints}`;
                                
                                return (
                                  <>
                                    {visibleMetrics.clicks && (
                                      <>
                                        <path d={clicksArea} fill="url(#clicksGradient)" />
                                        <path d={clicksPath} fill="none" stroke="var(--brand-color)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                    {visibleMetrics.impressions && (
                                      <>
                                        <path d={impressionsArea} fill="url(#impressionsGradient)" />
                                        <path d={impressionsPath} fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                    {visibleMetrics.ctr && (
                                      <>
                                        <path d={ctrArea} fill="url(#ctrGradient)" />
                                        <path d={ctrPath} fill="none" stroke="#34a853" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                    {visibleMetrics.position && (
                                      <>
                                        <path d={positionArea} fill="url(#positionGradient)" />
                                        <path d={positionPath} fill="none" stroke="#9c27b0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </svg>
                            {hoveredData && (
                              <div 
                                className="chart-tooltip"
                                style={{
                                  left: `${tooltipPosition.x}px`,
                                  top: `${tooltipPosition.y}px`
                                }}
                              >
                                <div className="tooltip-date">
                                  {new Date(hoveredData.date).toLocaleDateString('en-US', { 
                                    weekday: 'short',
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </div>
                                <div className="tooltip-metrics">
                                  {visibleMetrics.clicks && (
                                    <div className="tooltip-metric">
                                      <span className="tooltip-label">Clicks:</span>
                                      <span className="tooltip-value">{formatNumber(hoveredData.clicks)}</span>
                                    </div>
                                  )}
                                  {visibleMetrics.impressions && (
                                    <div className="tooltip-metric">
                                      <span className="tooltip-label">Impressions:</span>
                                      <span className="tooltip-value">{formatNumber(hoveredData.impressions)}</span>
                                    </div>
                                  )}
                                  {visibleMetrics.ctr && (
                                    <div className="tooltip-metric">
                                      <span className="tooltip-label">CTR:</span>
                                      <span className="tooltip-value">{formatPercentage(hoveredData.ctr)}</span>
                                    </div>
                                  )}
                                  {visibleMetrics.position && (
                                    <div className="tooltip-metric">
                                      <span className="tooltip-label">Position:</span>
                                      <span className="tooltip-value">{hoveredData.position.toFixed(1)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="line-chart-x-axis">
                              {performanceData.dailyData.map((day, index) => {
                                const totalDays = performanceData.dailyData.length;
                                const showEvery = Math.max(1, Math.floor(totalDays / 10));
                                if (index % showEvery === 0 || index === totalDays - 1) {
                                  return (
                                    <div key={index} className="x-axis-label">
                                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                          <div className="chart-legend">
                            {visibleMetrics.clicks && (
                              <div className="legend-item">
                                <div className="legend-line legend-clicks"></div>
                                <span>Clicks</span>
                              </div>
                            )}
                            {visibleMetrics.impressions && (
                              <div className="legend-item">
                                <div className="legend-line legend-impressions"></div>
                                <span>Impressions</span>
                              </div>
                            )}
                            {visibleMetrics.ctr && (
                              <div className="legend-item">
                                <div className="legend-line legend-ctr"></div>
                                <span>CTR</span>
                              </div>
                            )}
                            {visibleMetrics.position && (
                              <div className="legend-item">
                                <div className="legend-line legend-position"></div>
                                <span>Position</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-data">
                    <p>No performance data available for the selected date range.</p>
                  </div>
                )}
              </div>
            )}

            {/* Debug Section */}

            {/* Tracked Keywords Table */}
            {connected && selectedProperty && keywords.length > 0 && (
              <div className="tracked-keywords-section">
                <div className="tracked-keywords-header">
                  <h2 className="tracked-keywords-title">Tracked Keywords</h2>
                  <button
                    className="refresh-button"
                    onClick={loadTrackedKeywordsData}
                    disabled={loadingTrackedKeywords}
                    title="Refresh keyword data"
                  >
                    {loadingTrackedKeywords ? (
                      <>
                        <div className="button-spinner-small"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="23 4 23 10 17 10"></polyline>
                          <polyline points="1 20 1 14 7 14"></polyline>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                        <span>Refresh</span>
                      </>
                    )}
                  </button>
                </div>
                {loadingTrackedKeywords ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading keyword data...</p>
                  </div>
                ) : trackedKeywordsData.length > 0 ? (
                  <div className="tracked-keywords-table-container">
                    <table className="tracked-keywords-table">
                      <thead>
                        <tr>
                                  <th>Position</th>
                                  <th>Tracked Keywords</th>
                                  <th>Search Volume</th>
                                  <th>Competition</th>
                                  <th>URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackedKeywordsData.map((row, index) => {
                          const realSearchVolume = keywordSearchVolumes[row.keyword];
                          const competition = keywordCompetitions[row.keyword];
                          // eslint-disable-next-line no-unused-vars
                          const displayVolume = realSearchVolume !== null && realSearchVolume !== undefined 
                            ? realSearchVolume 
                            : row.volume; // Fallback to impressions if search volume not available
                          
                          return (
                            <tr key={index}>
                              <td className="position-cell">
                                {row.position > 0 ? row.position.toFixed(1) : '-'}
                              </td>
                              <td className="keyword-cell">{row.keyword}</td>
                              <td className="volume-cell">
                                {realSearchVolume !== null && realSearchVolume !== undefined ? (
                                  <span title="Real search volume from Google Ads API">
                                    {formatNumber(realSearchVolume)}
                                  </span>
                                ) : (
                                  <span title="Impressions from Search Console (last 30 days)">
                                    {formatNumber(row.volume)}
                                  </span>
                                )}
                              </td>
                              <td className="competition-cell">
                                {competition ? (
                                  <span className={`competition-badge competition-${competition.toLowerCase()}`}>
                                    {competition}
                                  </span>
                                ) : (
                                  <span className="muted">-</span>
                                )}
                              </td>
                              <td className="url-cell">
                                <a 
                                  href={row.targetUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="url-link"
                                >
                                  {row.targetUrl}
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>No keyword data available. Make sure you have keywords added in the Keyword Analysis page.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankTracking;
